import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Ruby's Organics",
  industry: ["Personal Products", "Beauty & Cosmetics", "Organic Makeup"],
  sub_industry: ["Organic Makeup", "Clean Beauty"],
  founded_year: 2015,
  founded_original_name: "Ruby's Organics",
  headquarters: ["Mumbai, Maharashtra, India"],
  tagline_primary: "India's first organic makeup brand",
  mission: '"Bringing clean, plant-based beauty that nourishes skin and celebrates individuality—believing everyone deserves to feel beautiful."',
  vision: '"To become a leading name in the global organic cosmetics market through product excellence and sustainable beauty."',
  overview:
    "Ruby's Organics is India's first organic makeup brand with clean, plant-based formulas for Indian skin. The company offers foundation, lipstick, mascara, and skincare through its website and Nykaa, with Ecocert and COSMOS certification.",
  scale: {
    revenue: 3.93,
    employees_estimate: 50,
    markets_served_count: 2,
    operating_countries_count: 11,
  },
  global_presence: {
    regions: [
      {
        region: "India",
        headquarters: "Mumbai, Maharashtra, India",
        active_markets: ["India", "Maharashtra", "Karnataka", "Delhi", "West Bengal", "Puducherry"],
        market_type: "Direct-to-Consumer (D2C), E-commerce, Retail Partnerships",
      },
      {
        region: "International E-commerce",
        headquarters: "Not found",
        active_markets: ["United States", "Singapore", "Australia", "Canada", "Bahrain", "Kuwait", "Oman", "Qatar", "United Arab Emirates", "Malaysia", "Hong Kong"],
        market_type: "Direct E-commerce (International Store at rubysorganics.store)",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "India",
        share_percent: 100.0,
        notes: "Approximately 100% of revenue (primary market); Growing at an impressive rate of 10 to 15 percent per month (as reported by Forbes)",
      },
      {
        region: "Direct-to-Consumer (D2C) Channel",
        share_percent: 58.0,
        notes: "58% of sales",
      },
      {
        region: "Marketplace Channel (Nykaa, Amazon, etc.)",
        share_percent: 42.0,
        notes: "42% of sales",
      },
      {
        region: "International Markets",
        share_percent: 0.0,
        notes: "Emerging market; specific percentage not disclosed (launched international shipping recently)",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Ruby Purple",
          hex: "#9B59B6",
        },
        {
          name: "Deep Black",
          hex: "#000000",
        },
        {
          name: "Clean White",
          hex: "#FFFFFF",
        },
      ],
      secondary: [
        {
          name: "Soft Beige/Cream",
          hex: "#F5F5F0",
        },
        {
          name: "Warm Taupe",
          hex: "#D4C5B9",
        },
      ],
      accent: [
        {
          name: "Organic Green",
          hex: "#8FBC8F",
        },
        {
          name: "Soft Gold",
          hex: "#D4AF37",
        },
      ],
    },
    typography: [],
    logos: [
      {
        variant: "Primary",
        url: "https://assets.zocket.com/uploads/4f126ce1-e914-47aa-8256-c602d325af97-ruby_logo.png",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Honest & Transparent", "Empowering", "Clean & Uncomplicated", "Inclusive", "Natural & Organic", "Confident & Authentic", "Educational"],
    taglines: [
      "India's First Organic Makeup brand",
      "Built like Skincare. Wears like Makeup.",
      "Clean, plant-based formulas that are high-performing, nourishing, and gentle on skin",
      "Clean, uncomplicated, and purposeful products built on innovative plant-based formulas",
    ],
    messaging_dos: ["Emphasize natural and organic ingredients", "Promote inclusivity and self-expression", "Highlight transparency and honesty in product claims"],
    messaging_donts: ["Avoid exaggerated claims or 'miracle' language", "Do not use synthetic/chemical-focused terminology", "Avoid exclusionary beauty standards language"],
    banned_words: [],
  },
  creative_assets: [
    "https://assets.zocket.com/uploads/d7474763-bc73-487c-a5bf-c2d77cc7893c-imgi_35_613697734_902728682227725_5818701678563194996_n.jpg",
    "https://assets.zocket.com/uploads/82f15106-ccf7-4d90-8e09-5c40f0f9c1d7-imgi_61_571526997_1354656992966193_7146137378187499712_n.jpg",
    "https://assets.zocket.com/uploads/bf55bea7-bae6-4fd0-beee-4992dffc1971-imgi_155_615418827_2019193578868202_6395473292187091573_n.jpg",
    "https://assets.zocket.com/uploads/41623291-2e92-446e-9b1a-967f9de5aa2f-WhatsApp_Image_2026-02-16_at_12.28.55_PM.jpeg",
    "https://assets.zocket.com/uploads/7fc93a18-3f0a-4145-a57c-cf3f851f0709-WhatsApp_Image_2026-02-16_at_12.30.27_PM.jpeg",
  ],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "High",
      patterns_learned: 100,
      avg_generation: "High",
    },
    style_drift_alerts: [
      {
        title: "Logo Style Drift",
        description: "Logo style has drifted from the original design",
        severity: "medium",
      },
      {
        title: "Color Scheme Drift",
        description: "Color scheme has drifted from the original design",
        severity: "medium",
      },
    ],
    preference_learning: [
      {
        preference: "Ruby's Organic Foundation",
        score: 90,
        change: 10,
      },
      {
        preference: "Ruby's Organic Lipstick",
        score: 90,
        change: 10,
      },
      {
        preference: "Ruby's Organic Mascara",
        score: 90,
        change: 10,
      },
      {
        preference: "Ruby's Organic Skincare",
        score: 90,
        change: 10,
      },
    ],
  },
  brand_architecture: {
    available_brands: ["Ruby's Organics"],
    selected_brand: "Ruby's Organics",
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.8,
    sentiment_graph: [
      {
        metric: "Customer Satisfaction Score",
        value: "4.8/5.0",
        delta: "+0.2",
      },
      {
        metric: "Market Share (Organic Makeup)",
        value: "~2-3%",
        delta: "+0.5%",
      },
      {
        metric: "D2C Channel Dominance",
        value: "58%",
        delta: "+8%",
      },
      {
        metric: "Post-Shark Tank Valuation",
        value: "₹12.5 Cr",
        delta: "+4%",
      },
      {
        metric: "Revenue Growth Rate",
        value: "10-15% MoM",
        delta: "+3%",
      },
      {
        metric: "Brand Recognition Score",
        value: "72/100",
        delta: "+15",
      },
      {
        metric: "Organic Traffic",
        value: "24.7K/month",
        delta: "+12%",
      },
      {
        metric: "Instagram Engagement",
        value: "99K followers",
        delta: "+18K",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["Last 12 months (2024-2025)"],
    themes: {
      positive: [
        "Clean Beauty Pioneer Status",
        "Product Quality & Formulation",
        "Skin-Friendly & Gentle",
        "Suitable for Indian Skin Tones",
        "ECOCERT/COSMOS Certification",
        "Moisturizing Properties",
        "Unique Packaging Innovation",
      ],
      neutral: ["Price Point Considerations", "Learning Curve for Application", "Buildable Coverage Requirement", "Shade Matching Complexity", "Limited Product Range"],
      negative: [
        "Staying Power Issues",
        "Fragrance Concerns",
        "Mascara Clumping",
        "Dryness on Application",
        "Customer Service Response Time",
        "Variable Product Performance",
        "Eye Sensitivity Issues",
      ],
    },
  },
  distribution: [
    {
      brand: "Mamaearth",
      share: 28.0,
    },
    {
      brand: "Plum Goodness",
      share: 18.0,
    },
    {
      brand: "Sugar Cosmetics",
      share: 15.0,
    },
    {
      brand: "Forest Essentials",
      share: 12.0,
    },
    {
      brand: "Others",
      share: 27.0,
    },
  ],
  sentiment_by_product: [
    {
      product: "Lipstick Range",
      sentiment: 8.2,
      change_delta: 5.0,
    },
    {
      product: "Skin Tint Mattifying Foundation",
      sentiment: 8.5,
      change_delta: 8.0,
    },
    {
      product: "Smoked Kohl + Brow Filler",
      sentiment: 8.7,
      change_delta: 6.0,
    },
    {
      product: "Hybrid Mascara",
      sentiment: 7.4,
      change_delta: 2.0,
    },
    {
      product: "Hydra Liquid Concealer",
      sentiment: 8.0,
      change_delta: 4.0,
    },
    {
      product: "Compact Setting Powder",
      sentiment: 7.8,
      change_delta: 3.0,
    },
    {
      product: "Lash + Brow Growth Oil",
      sentiment: 8.3,
      change_delta: 7.0,
    },
    {
      product: "Crème Blush Duo",
      sentiment: 8.1,
      change_delta: 0.0,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Organic & Natural Ingredients",
        weight: 10.0,
      },
      {
        topic: "Skin-Friendly & Non-Irritating",
        weight: 9.0,
      },
      {
        topic: "Suitable for Indian Skin Tones",
        weight: 8.0,
      },
      {
        topic: "ECOCERT/COSMOS Certification",
        weight: 7.0,
      },
      {
        topic: "Moisturizing & Nourishing",
        weight: 6.0,
      },
      {
        topic: "Cruelty-Free & Vegetarian",
        weight: 5.0,
      },
      {
        topic: "Unique Packaging Design",
        weight: 4.0,
      },
    ],
    complaints: [
      {
        topic: "High Price Point",
        weight: 10.0,
      },
      {
        topic: "Requires Lip Prep/Tugs on Application",
        weight: 7.0,
      },
      {
        topic: "Poor Staying Power",
        weight: 6.0,
      },
      {
        topic: "Fragrance Issues",
        weight: 5.0,
      },
      {
        topic: "Mascara Clumping",
        weight: 4.0,
      },
      {
        topic: "Limited Shade Range",
        weight: 3.0,
      },
      {
        topic: "Customer Service Response",
        weight: 2.0,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Mamaearth",
      threat_level: "high",
      market_share: 10.0,
      sentiment: 75.0,
    },
    {
      name: "Plum Goodness",
      threat_level: "high",
      market_share: 6.0,
      sentiment: 78.0,
    },
    {
      name: "Juicy Chemistry",
      threat_level: "medium-high",
      market_share: 22.5,
      sentiment: 80.0,
    },
    {
      name: "Renee Cosmetics",
      threat_level: "high",
      market_share: 5.0,
      sentiment: 72.0,
    },
    {
      name: "Color Chemistry",
      threat_level: "medium",
      market_share: 2.5,
      sentiment: 77.0,
    },
    {
      name: "Just Herbs",
      threat_level: "medium",
      market_share: 3.5,
      sentiment: 74.0,
    },
    {
      name: "Organic Harvest",
      threat_level: "medium",
      market_share: 2.5,
      sentiment: 70.0,
    },
    {
      name: "Asa Beauty",
      threat_level: "low-medium",
      market_share: 1.5,
      sentiment: 73.0,
    },
  ],
  secondary_competitors: [
    {
      name: "Forest Essentials",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "Kama Ayurveda",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "SoulTree",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "The Derma Co.",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "Minimalist",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "Bella Vita Organic",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
  ],
  share_of_voice: [
    {
      channel: "Social Media (Instagram/Facebook)",
      distribution: {
        Mamaearth: 18.0,
        "Plum Goodness": 13.0,
        "Sugar Cosmetics": 10.0,
        "Nykaa Beauty": 8.0,
        "Ruby's Organics": 6.0,
        "Juicy Chemistry": 5.0,
        Others: 40.0,
      },
    },
    {
      channel: "Digital Advertising (Search & Display)",
      distribution: {
        Mamaearth: 19.0,
        "Plum Goodness": 12.0,
        "Sugar Cosmetics": 10.0,
        "Nykaa Beauty": 8.0,
        "Ruby's Organics": 6.0,
        "Juicy Chemistry": 4.0,
        Others: 41.0,
      },
    },
    {
      channel: "E-commerce Platforms (Nykaa, Amazon)",
      distribution: {
        Mamaearth: 21.0,
        "Plum Goodness": 12.0,
        "Sugar Cosmetics": 11.0,
        "Nykaa Beauty": 9.0,
        "Ruby's Organics": 7.0,
        "Juicy Chemistry": 4.0,
        Others: 36.0,
      },
    },
    {
      channel: "Influencer Marketing",
      distribution: {
        Mamaearth: 17.0,
        "Plum Goodness": 14.0,
        "Sugar Cosmetics": 12.0,
        "Nykaa Beauty": 8.0,
        "Ruby's Organics": 6.0,
        "Juicy Chemistry": 5.0,
        Others: 38.0,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness Index 2024-2025",
      brands: [
        {
          brand_name: "Mamaearth",
          score: 85,
        },
        {
          brand_name: "Plum Goodness",
          score: 72,
        },
        {
          brand_name: "Forest Essentials",
          score: 68,
        },
        {
          brand_name: "Juicy Chemistry",
          score: 55,
        },
        {
          brand_name: "Ruby's Organics",
          score: 42,
        },
        {
          brand_name: "Renee Cosmetics",
          score: 58,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Rating 2024-2025",
      brands: [
        {
          brand_name: "Juicy Chemistry",
          score: 88,
        },
        {
          brand_name: "Plum Goodness",
          score: 82,
        },
        {
          brand_name: "Mamaearth",
          score: 78,
        },
        {
          brand_name: "Ruby's Organics",
          score: 80,
        },
        {
          brand_name: "Forest Essentials",
          score: 85,
        },
        {
          brand_name: "Kama Ayurveda",
          score: 83,
        },
      ],
    },
    {
      metric_name: "Net Promoter Score 2024-2025 (Converted)",
      brands: [
        {
          brand_name: "Juicy Chemistry",
          score: 75,
        },
        {
          brand_name: "Mamaearth",
          score: 70,
        },
        {
          brand_name: "Plum Goodness",
          score: 72,
        },
        {
          brand_name: "Ruby's Organics",
          score: 68,
        },
        {
          brand_name: "Forest Essentials",
          score: 73,
        },
        {
          brand_name: "Renee Cosmetics",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Product Quality & Efficacy Score 2024-2025",
      brands: [
        {
          brand_name: "Juicy Chemistry",
          score: 90,
        },
        {
          brand_name: "Forest Essentials",
          score: 88,
        },
        {
          brand_name: "Ruby's Organics",
          score: 85,
        },
        {
          brand_name: "Plum Goodness",
          score: 82,
        },
        {
          brand_name: "Mamaearth",
          score: 78,
        },
        {
          brand_name: "Kama Ayurveda",
          score: 86,
        },
      ],
    },
    {
      metric_name: "Brand Trust & Authenticity Score 2024-2025",
      brands: [
        {
          brand_name: "Forest Essentials",
          score: 87,
        },
        {
          brand_name: "Juicy Chemistry",
          score: 86,
        },
        {
          brand_name: "Kama Ayurveda",
          score: 84,
        },
        {
          brand_name: "Ruby's Organics",
          score: 78,
        },
        {
          brand_name: "Mamaearth",
          score: 76,
        },
        {
          brand_name: "Plum Goodness",
          score: 75,
        },
      ],
    },
    {
      metric_name: "Price-Value Perception Score 2024-2025",
      brands: [
        {
          brand_name: "Mamaearth",
          score: 82,
        },
        {
          brand_name: "Plum Goodness",
          score: 80,
        },
        {
          brand_name: "Bella Vita Organic",
          score: 85,
        },
        {
          brand_name: "Ruby's Organics",
          score: 72,
        },
        {
          brand_name: "Juicy Chemistry",
          score: 68,
        },
        {
          brand_name: "Forest Essentials",
          score: 60,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "First-mover advantage: India's first organic makeup brand with triple certification (ECOCERT/COSMOS/NATure)",
      "Niche positioning: Focused exclusively on organic makeup for Indian skin tones",
      "Quality credentials: Strong certifications differentiate from mass-market competitors",
      "Recent visibility: Shark Tank India Season 4 appearance, secured ₹1 Crore for 8% equity from Kunal Bahl",
      "D2C strength: 58% sales from D2C channels, 42% from marketplaces",
    ],
    improvement_areas: [
      "Scale gap: Significantly smaller than Mamaearth (₹3.93 Cr revenue vs Mamaearth's ₹554 Cr+ quarterly)",
      "Brand awareness: Lower social media following (99K vs 1M+ for top competitors)",
      "Share of voice: Limited digital advertising presence (~2-3% vs 15-20% for leaders)",
      "Distribution: Smaller retail footprint compared to established players",
      "Marketing budget: Limited resources vs well-funded competitors (Renee: $54.3M, Mamaearth: $89.4M raised)",
    ],
    disruption_watchlist: ["NatHabit", "Earth Rhythm", "Ilana Organics", "Conscious Chemist", "La Mior"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Ruby's Organics targets primarily urban, middle to upper-middle-class individuals in India, aged 18-35, with a strong inclination towards organic and eco-friendly beauty products. The audience is predominantly female, health-conscious, and digitally savvy, with a significant presence on social media platforms like Instagram and YouTube.",
  core_demographics: {
    primary_age_range: "18-35",
    gender_split: {
      female: 69.0,
      male: 31.0,
    },
    demographics_distribution: {
      age: [
        {
          age_range: "18-35",
          percentage: 69,
        },
        {
          age_range: "36-50",
          percentage: 25,
        },
        {
          age_range: "51-65",
          percentage: 6,
        },
      ],
      education: [
        {
          education_level: "High School",
          percentage: 52.5,
        },
        {
          education_level: "Bachelor's Degree",
          percentage: 38.5,
        },
        {
          education_level: "Master's Degree",
          percentage: 10,
        },
      ],
      geographic: [
        {
          location: "Urban",
          percentage: 50,
        },
        {
          location: "Suburban",
          percentage: 30,
        },
        {
          location: "Rural",
          percentage: 20,
        },
      ],
    },
    income_median_usd: 10800.0,
    location_type: "Urban",
  },
  occupation_distribution: [
    {
      occupation: "Students",
      percentage: 52.0,
    },
    {
      occupation: "Working Professionals",
      percentage: 30,
    },
    {
      occupation: "Homemakers",
      percentage: 18,
    },
  ],
  interests: {
    top_categories: [
      {
        category: "Organic/Clean Beauty",
        score: 9.5,
      },
      {
        category: "Skincare & Wellness",
        score: 9.0,
      },
      {
        category: "Sustainability & Eco-living",
        score: 8.5,
      },
      {
        category: "Social Media & Beauty Content",
        score: 8.5,
      },
      {
        category: "Ayurveda & Traditional Remedies",
        score: 8.0,
      },
      {
        category: "Fitness & Healthy Lifestyle",
        score: 7.5,
      },
      {
        category: "Fashion & Personal Style",
        score: 7.5,
      },
      {
        category: "Beauty Tutorials & Education",
        score: 8.0,
      },
      {
        category: "Vegan/Cruelty-free Products",
        score: 8.0,
      },
      {
        category: "Self-care & Mindfulness",
        score: 7.5,
      },
    ],
    brand_affinities: [
      {
        brand: "Mamaearth",
        affinity: 75.0,
      },
      {
        brand: "Nykaa",
        affinity: 70.0,
      },
      {
        brand: "Plum Goodness",
        affinity: 68.0,
      },
      {
        brand: "Forest Essentials",
        affinity: 65.0,
      },
      {
        brand: "The Body Shop",
        affinity: 62.0,
      },
      {
        brand: "Biotique",
        affinity: 60.0,
      },
      {
        brand: "Kama Ayurveda",
        affinity: 58.0,
      },
      {
        brand: "Minimalist",
        affinity: 55.0,
      },
      {
        brand: "Juicy Chemistry",
        affinity: 52.0,
      },
      {
        brand: "WOW Skin Science",
        affinity: 50.0,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        direct_website: 38.3,
        online_marketplaces: 43.4,
        retail_partnerships: 11.2,
        international_ecommerce: 7.1,
      },
      purchase_frequency: "2.5-3 times per year",
      avg_order_value_usd: 30.0,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Social Discovery",
          description: "Discovers brand through Instagram reels, influencer content, or peer recommendations",
          duration_days: 2,
        },
        {
          stage: 2,
          name: "Ingredient Investigation",
          description: "Conducts online research through Search and YouTube, checking ingredient lists, certifications, and reviews",
          duration_days: 5,
        },
        {
          stage: 3,
          name: "Comparison & Validation",
          description: "Double-checks decisions online, compares with alternatives, watches tutorials and product demonstrations",
          duration_days: 3,
        },
        {
          stage: 4,
          name: "Transaction",
          description: "Makes purchase through preferred channel (website or marketplace), often influenced by offers or influencer codes",
          duration_days: 1,
        },
        {
          stage: 5,
          name: "Engagement & Advocacy",
          description: "Shares experience on social media, provides reviews, becomes repeat customer or brand advocate",
          duration_days: 18,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        mobile: 72.5,
        desktop: 22.5,
        tablet: 5.0,
      },
      media_channels: {
        instagram: 34.6,
        youtube: 25.3,
        facebook: 15.2,
        google_search: 10.1,
        whatsapp: 6.0,
        amazon: 5.5,
        other: 3.2,
      },
    },
  },
  platforms: {
    distribution: {
      instagram: 35.0,
      youtube: 25.0,
      facebook: 15.0,
      google_search: 10.0,
      whatsapp: 5.0,
      amazon: 5.0,
      other: 5.0,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 99000,
      health_score: 85.0,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 70.0,
    },
    {
      platform: "LinkedIn",
      followers: 0,
      health_score: 70.0,
    },
    {
      platform: "Twitter/X",
      followers: 0,
      health_score: 40.0,
    },
    {
      platform: "Pinterest",
      followers: 2,
      health_score: 20.0,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0.0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0.0,
    },
    {
      platform: "Official Website + E-commerce Marketplaces",
      followers: 24700,
      health_score: 85.0,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 60.0,
    },
    {
      label: "Neutral",
      percentage: 30.0,
    },
    {
      label: "Negative",
      percentage: 10.0,
    },
  ],
  recent_comments: [
    {
      user: "beautylover123",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Love the new shades! Perfect for my skin tone.",
      postContext: "Post about new lipstick shades",
    },
    {
      user: "makeupguru",
      platform: "Facebook",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Interesting product, but I wish there were more color options.",
      postContext: "Discussion on product variety",
    },
    {
      user: "organicfan",
      platform: "Instagram",
      timeAgo: "3 days ago",
      status: "positive",
      commentText: "Finally, a brand that cares about natural ingredients!",
      postContext: "Post about organic ingredients",
    },
  ],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 3.0,
      },
      {
        platform: "Facebook",
        engagement_rate: 1.8,
      },
      {
        platform: "Video Commerce (Website)",
        engagement_rate: 8.8,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 1.2,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Video Try-Ons / Tutorials",
        performance: 9.0,
      },
      {
        content_type: "Reels (Instagram)",
        performance: 8.0,
      },
      {
        content_type: "User-Generated Content (UGC)",
        performance: 8.0,
      },
      {
        content_type: "Product Demos",
        performance: 7.0,
      },
      {
        content_type: "Influencer Partnerships",
        performance: 7.0,
      },
      {
        content_type: "Stories (Instagram)",
        performance: 6.0,
      },
      {
        content_type: "Educational Content / Brand Stories",
        performance: 6.0,
      },
    ],
    follower_growth_trend: [
      {
        month: "February",
        followers: 75000.0,
      },
      {
        month: "March",
        followers: 99000.0,
      },
    ],
  },
};

export const mBrandIntelligenceRubyOrganics: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
